import React from 'react';
import { useTranslation } from 'react-i18next';
import { Column, AutoSizer, Table } from 'react-virtualized';
import { Tag, Tooltip, Empty, Spin } from 'antd';
import {
  AccountConfigurationList,
  UsedCustomField,
} from '../../../../../types/account_configurations';
import { getTimezoneOffset, localDateString, localTimeString } from '../../../../utils/Utils';
import ConfigurationHeaderCell from './ConfigurationHeaderCell';

const ROW_HEIGHT = 50;

interface AccountDynamicFieldListsTableProps {
  filteredData: AccountConfigurationList[];
  isLoading: boolean;
  selectedListIds: number[];
  setSelectedListIds: (ids: number[]) => void;
  selectedUserIds: number[];
  setSelectedUserIds: (ids: number[]) => void;
  sortColumn: string | undefined;
  sortOrder: string | undefined;
  onSort: (column: string, order: string) => void;
  nameListValues: { id: number; name: string }[];
  userListValues: { id: number; name: string }[];
  userNameToIdsMap: Map<string, number[]>;
}

const AccountDynamicFieldListsTable: React.FC<AccountDynamicFieldListsTableProps> = ({
  filteredData,
  isLoading,
  selectedListIds,
  setSelectedListIds,
  selectedUserIds,
  setSelectedUserIds,
  sortColumn,
  sortOrder,
  onSort,
  nameListValues,
  userListValues,
  userNameToIdsMap,
}) => {
  const { t } = useTranslation();
  const timezoneOffset = React.useMemo(() => getTimezoneOffset(), []);

  const renderCreatedAt = (date: string) => {
    if (!date) return <span />;
    return (
      <span>
        {localDateString(date)} {localTimeString(date)}
      </span>
    );
  };

  const renderSingleUsedDynamicConfiguration = (customField: UsedCustomField) => {
    if (!customField) {
      return <span />;
    }

    try {
      const parsedDynamicConfiguration = customField.dynamic_configuration
        ? JSON.parse(customField.dynamic_configuration)
        : '';

      return (
        <Tooltip
          title={
            <div>
              <pre className="account-configurations__tooltip-json">
                {JSON.stringify(parsedDynamicConfiguration, null, 2)}
              </pre>
            </div>
          }
        >
          <Tag color="orange">{customField.name}</Tag>
        </Tooltip>
      );
    } catch (e) {
      return (
        <Tooltip title={customField.dynamic_configuration}>
          <Tag color="purple">{customField.name}</Tag>
        </Tooltip>
      );
    }
  };

  const renderUsedDynamicConfigurations = (customFields: UsedCustomField[] | null) => {
    if (!customFields || customFields.length === 0) {
      return <span />;
    }

    if (customFields.length === 1) {
      return renderSingleUsedDynamicConfiguration(customFields[0]);
    }

    return (
      <div>
        {customFields.map(field => (
          <div key={field.id}>{renderSingleUsedDynamicConfiguration(field)}</div>
        ))}
      </div>
    );
  };

  const createNameHeaderCell = () => (
    <ConfigurationHeaderCell
      label={t('configurations:name')}
      filterValues={nameListValues}
      handleFilterChange={setSelectedListIds}
      selectedFilterIds={selectedListIds}
      showSearch
      sortColumn="name"
      currentSortColumn={sortColumn}
      sortOrder={sortOrder}
      onSort={order => onSort('name', order)}
    />
  );

  const createCreatedByHeaderCell = () => (
    <ConfigurationHeaderCell
      label={t('configurations:createdBy')}
      filterValues={userListValues}
      handleFilterChange={userIds => {
        let allSelectedUserIds: number[] = [];
        userIds.forEach(userId => {
          const userName = userListValues.find(u => u.id === userId)?.name;
          if (userName) {
            const ids = userNameToIdsMap.get(userName) || [];
            allSelectedUserIds.push(...ids);
          }
        });
        allSelectedUserIds = [...new Set(allSelectedUserIds)];
        setSelectedUserIds(allSelectedUserIds);
      }}
      selectedFilterIds={selectedUserIds}
      showSearch
      sortColumn="created_by"
      currentSortColumn={sortColumn}
      sortOrder={sortOrder}
      onSort={order => onSort('created_by', order)}
    />
  );

  return (
    <div className="h-full w-full overflow-hidden bg-white">
      <AutoSizer>
        {({ height, width }) => (
          <div style={{ height, width }}>
            <Table
              key={`table-${filteredData.length}`}
              height={height}
              width={width - 2}
              headerHeight={ROW_HEIGHT}
              rowHeight={ROW_HEIGHT}
              rowCount={filteredData.length}
              rowGetter={({ index }) => filteredData[index]}
              noRowsRenderer={() =>
                isLoading ? (
                  <Spin className="spinner-center" style={{ marginTop: '20px' }} />
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t('configurations:noDynamicLists')}
                  />
                )
              }
            >
              <Column label={t('configurations:id')} dataKey="id" width={100} />
              <Column
                headerRenderer={createNameHeaderCell}
                dataKey="name"
                width={250}
                flexGrow={1}
              />
              <Column
                label={`${t('configurations:createdAt')} (${timezoneOffset})`}
                dataKey="created_at"
                width={150}
                cellRenderer={({ cellData }) => renderCreatedAt(cellData)}
              />
              <Column
                headerRenderer={createCreatedByHeaderCell}
                dataKey="created_by"
                width={250}
                cellRenderer={({ cellData }) => cellData}
              />
              <Column
                label={t('configurations:usedDynamicConfigurations')}
                dataKey="used_custom_fields"
                width={300}
                cellRenderer={({ cellData }) => renderUsedDynamicConfigurations(cellData)}
              />
            </Table>
          </div>
        )}
      </AutoSizer>
    </div>
  );
};

export default AccountDynamicFieldListsTable;
